import { cloneDeep, flatMap, get, isEmpty, values, sortBy, first, last } from 'lodash'
import moment from 'moment'
import * as PropTypes from 'prop-types'
import React, { PureComponent } from 'react'
import { connect } from 'react-redux'
import { ActionCreators } from 'redux-undo'
import { Viewer } from '@teqplay/viewer'
import produce from 'immer'

import { QUAY_ROLES, SEA_SHIP_SHADOW_CONFIGURATION } from '../../js/constants'
import { findClosestStartingShift } from '../../utils/terminalVisitManningUtils'
import {
  hasParallelOrders,
  getNewEmptyVisit,
  getClosestQuayToBollard,
  isVisitInNormalQuay,
  getSortedOrders
} from '../../utils/terminalVisitUtils'
import {
  onDateChange,
  setActiveOverlays,
  setConflictModalConflictsInStore,
  setConflictModalDimensionsInStore,
  setSelectedMaintenance,
  updateTerminalVisitInBackend,
  setTerminalVisits,
  setSelectedVisit
} from '../app/actions'
import ErrorBoundary from '../errorBoundary/ErrorBoundary'
import { setTerminalPlannerExpanded, setZoomLevel } from '../ui/actions'
import { getAtdOrCargoEnd, getAtaOrCargoStart } from './../../utils/terminalVisitConflictsUtils'
import BollardAxis from './bollardAxis/BollardAxis'
import Controls from './controls/Controls'
import MaintenanceItems from './maintenanceitems/MaintenanceItems'
import ParallelTerminalVisit from './parallelTerminalVisit/ParallelTerminalVisit'
import PlanningGridLines from './planninggridlines/PlanningGridLines'
import PlanningNonWorkingHoursOverlay from './planningnonworkinghoursoverlay/PlanningNonWorkingHoursOverlay'
import PlanningPastOverlay from './planningpastoverlay/PlanningPastOverlay'
import SeaShipShadow from './seaShipShadow/SeaShipShadow'
import './TerminalPlanner.scss'
import TerminalVisit from './terminalVisit/TerminalVisit'
import TerminalVisitContextMenu from './terminalVisitContextMenu/TerminalVisitContextMenu'
import TerminalVisitLaycan from './terminalVisitLaycan/TerminalVisitLaycan'
import TimeStepLabels from './timeStepLabels/TimeStepLabels'
import { getGridTime } from '../../utils/general'
import {
  updateTerminalVisits,
  categorizeVisits,
  checkIfBollardPositionShouldUpdate
} from '../../utils/terminalPlannerUtils'
import { handleFetchError } from '../../js/sentry'
import { deleteTerminalVisitFromBackendRest } from '../../js/rest'
import AddNewButton from './addNewButton/AddNewButton'
import { userPermissionRoles } from '../../utils/userPermissions'
import { isValidTimestamp } from '../../utils/formUtils'

class TerminalPlanner extends PureComponent {
  constructor(props) {
    super(props)
    this.bollardAxisElement = React.createRef()

    this.state = {
      widthActiveQuay: 0,
      heightActiveQuay: 0,
      widthExternalQuay: 0,
      heightExternalQuay: 0,
      startTime: props.defaultShortTerm ? moment(new Date()).toDate() : new Date(props.app.present.dateRange.from),
      endTime: props.defaultShortTerm
        ? moment(new Date()).add(2, 'days').toDate()
        : new Date(props.app.present.dateRange.to),

      minZIndex: 0,
      zIndexMap: [],
      filterBoardBoard: false,

      draggingTerminalVisit: []
    }
  }

  static propTypes = {
    conflicts: PropTypes.object.isRequired,
    terminalVisitsWithOverlap: PropTypes.object.isRequired,
    externalTerminalVisitsWithOverlap: PropTypes.object.isRequired,
    customFetch: PropTypes.func.isRequired,
    isAllowedToEdit: PropTypes.bool,
    defaultShortTerm: PropTypes.bool
  }

  componentDidUpdate(prevProps) {
    // If last timeframe or min/max bollards are not the same as current timeframe or min/max bollards ->
    // update these values in state so that their new values are used for rendering
    if (
      !moment(prevProps.app.present.dateRange.from).isSame(this.props.app.present.dateRange.from) ||
      !moment(prevProps.app.present.dateRange.to).isSame(this.props.app.present.dateRange.to)
    ) {
      // Set timeframe state from given props
      this.setState({
        startTime: new Date(this.props.app.present.dateRange.from),
        endTime: new Date(this.props.app.present.dateRange.to)
      })
    }
  }

  /**
   * Clear TerminalVisit grouping when user clicks on grid
   */
  handleGridClick = e => {
    // class names from elements within TerminalViewer grid
    if (
      e.target &&
      ['right-panel', 'vertical-axis-grid', 'time-axis-grid-line', 'past-overlay', 'terminal-visits-wrapper'].includes(
        e.target.className
      )
    ) {
      this.setState({
        draggingTerminalVisit: []
      })
    }
  }

  render() {
    const bollardAxisHeight =
      this.bollardAxisElement && this.bollardAxisElement.current ? this.bollardAxisElement.current.clientHeight : 0
    if (!this.props.app.present.config.quays || !this.props.app.present.config.quays.length) {
      return <div>Unable to render quays</div>
    }

    const { planner: isPlanner, orderAdmin: isOrderAdmin } = userPermissionRoles(this.props.login.auth.roles)

    // filters out any quays which have EXTERNAL / LAYBY Type
    const allExternalQuays = this.props.app.present.config.quays.filter(quay => quay.role === QUAY_ROLES.external)
    // always get the first of all the external quays. For now we only support 1 external quay. We can change this later on.
    const externalQuayToShow = allExternalQuays ? allExternalQuays[0] : undefined
    const normalQuays = this.props.app.present.config.quays.filter(quay => quay.role === QUAY_ROLES.normal)

    if (!normalQuays.length) {
      return <div>Terminal must have at least one quay with role &apos;NORMAL&apos;</div>
    }

    // get the start / end bollard of the quays configured with role 'NORMAL'
    const startBollard = normalQuays[0].minBollard
    const endBollard = this.props.app.present.config.quays[normalQuays.length - 1].maxBollard

    // TODO: move the logic to check visibility to orders.map(...)
    const visibleTerminalVisits = this.props.app.present.terminalVisits.filter(terminalVisit => {
      const isDragging = this.state.draggingTerminalVisit.some(draggingVisit => draggingVisit._id === terminalVisit._id)
      if (isDragging) return true

      return terminalVisit.orders.some(order => {
        return (
          getAtaOrCargoStart(terminalVisit, order) < this.state.endTime.valueOf() &&
          getAtdOrCargoEnd(terminalVisit, order) > this.state.startTime.valueOf()
        )
      })
    })
    const filteredVisits = this.state.filterBoardBoard
      ? visibleTerminalVisits.filter(visit => visit.boardBoard === false)
      : visibleTerminalVisits

    const { externalQuayVisits, mainQuayVisits } = categorizeVisits(this.props.app.present.config.quays, filteredVisits)

    return (
      <ErrorBoundary>
        <div className="terminal-planner-container">
          <Controls
            config={this.props.app.present.config}
            terminalPlannerExpanded={this.props.ui.terminalPlannerExpanded}
            onChangeTerminalPlannerExpanded={() =>
              this.props.dispatch(setTerminalPlannerExpanded(!this.props.ui.terminalPlannerExpanded))
            }
            onChangeZoom={this.handleZoom}
            onSnapToZoomLevel={this.handleSnapToZoomLevel}
            amountOfUndoStates={this.props.app.past.length}
            amountOfRedoStates={this.props.app.future.length}
            onUndoLastAction={this.handleUndo}
            onRedoLastAction={this.handleRedo}
            onToggleAllLaycansOverlay={this.handleToggleOverlay}
            onToggleFilterBoardBoard={this.onToggleFilterBoardBoard}
            filterBoardBoard={this.state.filterBoardBoard}
            laycanActive={this.props.app.present.activeOverlays.includes('laycans')}
            showActions={this.props.isAllowedToEdit}
            terminalId={this.props.login.auth.terminalId}
          />

          <AddNewButton
            onClickAddVisit={this.handleClickAddVisit}
            onClickAddMaintenance={this.handleClickAddMaintenance}
          />

          <div className="terminal-viewer-wrapper">
            {externalQuayToShow && (
              <div className="collapsed-viewer">
                <Viewer
                  key={externalQuayToShow.name}
                  width={this.state.widthExternalQuay}
                  height={this.state.heightExternalQuay}
                  onResize={this.handleResizeExternalQuay}
                  xMargin={0}
                  xType={'Number'}
                  xStart={0}
                  xEnd={1}
                  yType={'Date'}
                  yStart={this.state.startTime}
                  yEnd={this.state.endTime}
                  onYChange={this.handleTimeChange}
                  onDraggingCompleted={this.handleStopDraggingTerminalVisit}
                >
                  {({ measureRef, yUnitToScreen, xUnitToScreen }) => (
                    <div className="terminal-viewer-contents">
                      <div className="top-panel with-label-offset" style={{ height: bollardAxisHeight }}>
                        <BollardAxis
                          bollardToScreen={xUnitToScreen}
                          config={this.props.app.present.config}
                          showExternal={true}
                        />
                      </div>
                      <div className="bottom-panel">
                        <div className="left-panel">
                          <TimeStepLabels
                            startTime={this.state.startTime}
                            endTime={this.state.endTime}
                            timeToScreen={yUnitToScreen}
                          />
                        </div>
                        <div className="right-panel" ref={measureRef} onClick={this.handleGridClick}>
                          <PlanningGridLines
                            startTime={this.state.startTime}
                            endTime={this.state.endTime}
                            quays={[externalQuayToShow]}
                            timeToScreen={yUnitToScreen}
                            bollardToScreen={xUnitToScreen}
                          />
                          <PlanningPastOverlay startTime={this.state.startTime} timeToScreen={yUnitToScreen} />
                          <PlanningNonWorkingHoursOverlay
                            startTime={this.state.startTime}
                            endTime={this.state.endTime}
                            nonWorkingHours={this.props.app.present.config.nonWorkingHours}
                            config={this.props.app.present.config}
                            timeToScreen={yUnitToScreen}
                          />

                          <div className="terminal-visits-wrapper">
                            {externalQuayVisits.map(terminalVisit => {
                              const isSelected = this.props.app.present.selectedVisit
                                ? this.props.app.present.selectedVisit._id === terminalVisit._id
                                : false

                              const isDragging =
                                this.state.draggingTerminalVisit &&
                                !!this.state.draggingTerminalVisit.find(visit => visit._id === terminalVisit._id)
                              const terminalVisitToShow = isDragging
                                ? this.state.draggingTerminalVisit.find(visit => visit._id === terminalVisit._id)
                                    .referenceTerminalVisit
                                : terminalVisit

                              if (hasParallelOrders(terminalVisit)) {
                                return (
                                  <TerminalVisit
                                    key={terminalVisit._id}
                                    terminalVisit={terminalVisitToShow}
                                    order={terminalVisit.orders[0]}
                                    conflicts={get(this.props.conflicts.externalConflicts, [
                                      terminalVisit._id,
                                      terminalVisit.orders[0].uuid
                                    ])}
                                    isOverlapping={
                                      get(this.props.externalTerminalVisitsWithOverlap, [terminalVisitToShow._id]) !==
                                      undefined
                                    }
                                    isSelected={isSelected}
                                    timeToScreen={yUnitToScreen}
                                    bollardToScreen={xUnitToScreen}
                                    onSelectionToggle={this.handleVisitGrouping}
                                    onChangeTerminalVisit={
                                      this.props.isAllowedToEdit ? this.handleChangeTerminalVisit : null
                                    }
                                    onSelectTerminalVisit={this.handleSelectTerminalVisit}
                                    zIndex={this.getZIndexForTerminalVisit(terminalVisit, this.state.zIndexMap)}
                                    onLayerDown={this.handleLayerDownTerminalVisit}
                                    isPartOfGrouping={
                                      this.state.draggingTerminalVisit &&
                                      !!this.state.draggingTerminalVisit.find(x => x._id === terminalVisit._id)
                                    }
                                    setConflictModalDimensions={dimensions =>
                                      this.props.dispatch(setConflictModalDimensionsInStore(dimensions))
                                    }
                                    setConflictModalConflicts={conflicts =>
                                      this.props.dispatch(setConflictModalConflictsInStore(conflicts))
                                    }
                                    userRoles={this.props.login.auth.roles}
                                    displayFields={this.props.app.present.config.cardDisplayFields}
                                    isCollapsedVisit={true}
                                    isExternalParallelVisit
                                    quays={this.props.app.present.config.quays}
                                  />
                                )
                              }

                              return terminalVisitToShow.orders.map(order => {
                                const compoundKey = terminalVisit._id + ':' + order.uuid
                                return (
                                  <TerminalVisit
                                    key={compoundKey}
                                    terminalVisit={terminalVisit}
                                    order={order}
                                    isOverlapping={get(this.props.externalTerminalVisitsWithOverlap, [
                                      terminalVisit._id,
                                      order.uuid
                                    ])}
                                    conflicts={get(this.props.conflicts.externalConflicts, [
                                      terminalVisit._id,
                                      order.uuid
                                    ])}
                                    isSelected={isSelected}
                                    timeToScreen={yUnitToScreen}
                                    bollardToScreen={xUnitToScreen}
                                    onSelectionToggle={this.handleVisitGrouping}
                                    onChangeTerminalVisit={
                                      this.props.isAllowedToEdit ? this.handleChangeTerminalVisit : null
                                    }
                                    onSelectTerminalVisit={this.handleSelectTerminalVisit}
                                    zIndex={this.getZIndexForTerminalVisit(terminalVisit, this.state.zIndexMap)}
                                    onLayerDown={this.handleLayerDownTerminalVisit}
                                    isPartOfGrouping={
                                      this.state.draggingTerminalVisit &&
                                      !!this.state.draggingTerminalVisit.find(x => x._id === terminalVisit._id)
                                    }
                                    setConflictModalDimensions={dimensions =>
                                      this.props.dispatch(setConflictModalDimensionsInStore(dimensions))
                                    }
                                    setConflictModalConflicts={conflicts =>
                                      this.props.dispatch(setConflictModalConflictsInStore(conflicts))
                                    }
                                    userRoles={this.props.login.auth.roles}
                                    displayFields={this.props.app.present.config.cardDisplayFields}
                                    isCollapsedVisit={true}
                                    quays={this.props.app.present.config.quays}
                                  />
                                )
                              })
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </Viewer>
              </div>
            )}

            <div className="selected-viewer">
              <Viewer
                width={this.state.widthActiveQuay}
                height={this.state.heightActiveQuay}
                onResize={this.handleResizeActiveQuay}
                xMargin={0}
                xType={'Number'}
                xStart={startBollard}
                xEnd={endBollard}
                yType={'Date'}
                yStart={this.state.startTime}
                yEnd={this.state.endTime}
                onYChange={this.handleTimeChange}
                onDraggingCompleted={this.handleStopDraggingTerminalVisit}
              >
                {({ measureRef, yUnitToScreen, xUnitToScreen }) => (
                  <div className="terminal-viewer-contents">
                    <div
                      className={`top-panel ${!externalQuayToShow ? 'with-label-offset' : ''}`}
                      ref={this.bollardAxisElement}
                    >
                      <BollardAxis
                        bollardToScreen={xUnitToScreen}
                        config={this.props.app.present.config}
                        showExternal={false}
                      />
                    </div>
                    <div className="bottom-panel">
                      {!externalQuayToShow && (
                        <div className="left-panel">
                          <TimeStepLabels
                            startTime={this.state.startTime}
                            endTime={this.state.endTime}
                            timeToScreen={yUnitToScreen}
                          />
                        </div>
                      )}
                      <div className="right-panel" ref={measureRef} onClick={this.handleGridClick}>
                        <PlanningGridLines
                          startTime={this.state.startTime}
                          endTime={this.state.endTime}
                          quays={normalQuays}
                          timeToScreen={yUnitToScreen}
                          bollardToScreen={xUnitToScreen}
                        />
                        <PlanningPastOverlay startTime={this.state.startTime} timeToScreen={yUnitToScreen} />
                        <PlanningNonWorkingHoursOverlay
                          startTime={this.state.startTime}
                          endTime={this.state.endTime}
                          nonWorkingHours={this.props.app.present.config.nonWorkingHours}
                          config={this.props.app.present.config}
                          timeToScreen={yUnitToScreen}
                        />
                        <MaintenanceItems
                          maintenanceItems={this.props.app.present.terminalMaintenance}
                          onSelectMaintenanceItem={maintenance =>
                            this.props.dispatch(setSelectedMaintenance(maintenance))
                          }
                          selectedMaintenance={this.props.app.present.selectedMaintenance}
                          timeToScreen={yUnitToScreen}
                          bollardToScreen={xUnitToScreen}
                        />
                        {SEA_SHIP_SHADOW_CONFIGURATION.filter(({ terminalId }) => {
                          return this.props.login.auth.terminalId === terminalId
                        }).map(shadowConfig => {
                          return this.renderSeaShipShadows(mainQuayVisits, shadowConfig, yUnitToScreen, xUnitToScreen)
                        })}
                        {this.props.app.present.activeOverlays.indexOf('laycans') !== -1 ? (
                          <div className="terminal-visits-laycan-wrapper">
                            {mainQuayVisits.map(terminalVisit => {
                              const draggingVisit = this.state.draggingTerminalVisit.find(
                                v => v._id === terminalVisit._id
                              )

                              const terminalVisitToShow = draggingVisit
                                ? draggingVisit.referenceTerminalVisit
                                : terminalVisit

                              return terminalVisitToShow.orders.map(order => {
                                const compoundKey = terminalVisit._id + ':' + order.uuid

                                return (
                                  <TerminalVisitLaycan
                                    key={compoundKey}
                                    terminalVisit={terminalVisit}
                                    order={order}
                                    timeToScreen={yUnitToScreen}
                                    bollardToScreen={xUnitToScreen}
                                  />
                                )
                              })
                            })}
                          </div>
                        ) : null}

                        <div className="terminal-visits-wrapper">
                          {mainQuayVisits.map(terminalVisit => {
                            const isSelected = this.props.app.present.selectedVisit
                              ? this.props.app.present.selectedVisit._id === terminalVisit._id
                              : false

                            if (terminalVisit.parallelGroups > 1) {
                              const conflictsPerOrder = get(this.props.conflicts.conflicts, [terminalVisit._id])
                              const conflicts = conflictsPerOrder ? flatMap(values(conflictsPerOrder)) : undefined

                              const draggingVisit = this.state.draggingTerminalVisit.find(
                                visit => visit._id === terminalVisit._id
                              )

                              const terminalVisitToShow = draggingVisit
                                ? draggingVisit.referenceTerminalVisit
                                : terminalVisit

                              return (
                                <ParallelTerminalVisit
                                  key={terminalVisitToShow._id}
                                  terminalVisit={terminalVisitToShow}
                                  conflicts={conflicts}
                                  timeToScreen={yUnitToScreen}
                                  bollardToScreen={xUnitToScreen}
                                  userRoles={this.props.login.auth.roles}
                                  isSelected={isSelected}
                                  isOverlapping={
                                    get(this.props.terminalVisitsWithOverlap, [terminalVisitToShow._id]) !== undefined
                                  }
                                  isPartOfGrouping={!!draggingVisit}
                                  onSelectionToggle={this.handleVisitGrouping}
                                  zIndex={this.getZIndexForTerminalVisit(terminalVisitToShow, this.state.zIndexMap)}
                                  onLayerDown={this.handleLayerDownTerminalVisit}
                                  onDrag={this.props.isAllowedToEdit ? this.handleDragTerminalVisit : null}
                                  onVisitChange={this.props.isAllowedToEdit ? this.handleParallelVisitChanged : null}
                                  setConflictModalDimensions={dimensions =>
                                    this.props.dispatch(setConflictModalDimensionsInStore(dimensions))
                                  }
                                  setConflictModalConflicts={conflicts =>
                                    this.props.dispatch(setConflictModalConflictsInStore(conflicts))
                                  }
                                  onSelect={this.handleSelectTerminalVisit}
                                  displayFields={this.props.app.present.config.cardDisplayFields}
                                  quays={this.props.app.present.config.quays}
                                />
                              )
                            }

                            const draggingVisit = this.state.draggingTerminalVisit.find(
                              visit => visit._id === terminalVisit._id
                            )
                            const terminalVisitToShow = draggingVisit
                              ? draggingVisit.referenceTerminalVisit
                              : terminalVisit

                            return terminalVisitToShow.orders.map(order => {
                              const compoundKey = terminalVisit._id + ':' + order.uuid
                              return (
                                <TerminalVisit
                                  key={compoundKey}
                                  terminalVisit={terminalVisitToShow}
                                  order={order}
                                  conflicts={get(this.props.conflicts.conflicts, [terminalVisit._id, order.uuid])}
                                  isOverlapping={get(this.props.terminalVisitsWithOverlap, [
                                    terminalVisit._id,
                                    order.uuid
                                  ])}
                                  isSelected={isSelected}
                                  timeToScreen={yUnitToScreen}
                                  bollardToScreen={xUnitToScreen}
                                  onSelectionToggle={this.handleVisitGrouping}
                                  onChangeTerminalVisit={
                                    this.props.isAllowedToEdit ? this.handleChangeTerminalVisit : null
                                  }
                                  onSelectTerminalVisit={this.handleSelectTerminalVisit}
                                  zIndex={this.getZIndexForTerminalVisit(terminalVisit, this.state.zIndexMap)}
                                  onLayerDown={this.handleLayerDownTerminalVisit}
                                  isPartOfGrouping={!!draggingVisit}
                                  setConflictModalDimensions={dimensions =>
                                    this.props.dispatch(setConflictModalDimensionsInStore(dimensions))
                                  }
                                  setConflictModalConflicts={conflicts =>
                                    this.props.dispatch(setConflictModalConflictsInStore(conflicts))
                                  }
                                  userRoles={this.props.login.auth.roles}
                                  displayFields={this.props.app.present.config.cardDisplayFields}
                                  quays={this.props.app.present.config.quays}
                                />
                              )
                            })
                          })}
                        </div>
                      </div>
                    </div>

                    {isPlanner || isOrderAdmin ? (
                      <TerminalVisitContextMenu
                        config={this.props.app.present.config}
                        authToken={this.props.login.auth.token}
                        customFetch={this.props.customFetch}
                        dispatch={this.props.dispatch}
                        quays={this.props.app.present.config.quays}
                        selectedVisits={this.state.draggingTerminalVisit || []}
                        deleteMultipleTerminalVisits={this.deleteMultipleTerminalVisits}
                        clearSelectedVisits={() => {
                          this.setState({
                            draggingTerminalVisit: []
                          })
                        }}
                      />
                    ) : null}
                  </div>
                )}
              </Viewer>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    )
  }

  renderSeaShipShadows = (terminalVisits, shadowConfig, timeToScreen, bollardToScreen) => {
    if (!shadowConfig) {
      return null
    }

    const quay = this.props.app.present.config.quays.find(quay => {
      return quay.name === shadowConfig.quay
    })

    if (!quay) {
      console.error(`No quay named "${shadowConfig.quay}" found for sea ship shadows. Configuration:`, shadowConfig)

      return null
    }

    return (
      <div key={shadowConfig.quay} className="seaship-shadows-wrapper">
        {terminalVisits.map(terminalVisit => {
          const draggingVisit = this.state.draggingTerminalVisit.find(v => v._id === terminalVisit._id)

          const terminalVisitToUse = draggingVisit ? draggingVisit.referenceTerminalVisit : terminalVisit

          const shadowVisitOrders = terminalVisitToUse.orders.filter(
            order => order.bollardEnd > quay.minBollard && order.bollardStart < quay.maxBollard
          )
          return (
            shadowVisitOrders &&
            shadowVisitOrders.length > 0 && (
              <SeaShipShadow
                key={terminalVisitToUse._id}
                terminalVisit={terminalVisitToUse}
                orders={shadowVisitOrders}
                timeToScreen={timeToScreen}
                bollardToScreen={bollardToScreen}
                offset={shadowConfig.offset}
                minBollard={shadowConfig.minBollard}
                maxBollard={shadowConfig.maxBollard}
              />
            )
          )
        })}
      </div>
    )
  }

  handleUndo = () => {
    this.setState({
      draggingTerminalVisit: []
    })
    this.props.dispatch(ActionCreators.undo())
  }
  handleRedo = () => {
    this.setState({
      draggingTerminalVisit: []
    })
    this.props.dispatch(ActionCreators.redo())
  }

  handleResizeActiveQuay = dimensions => {
    this.setState({
      widthActiveQuay: dimensions.width,
      heightActiveQuay: dimensions.height
    })
  }

  handleResizeExternalQuay = dimensions => {
    this.setState({
      widthExternalQuay: dimensions.width,
      heightExternalQuay: dimensions.height
    })
  }

  handleSelectTerminalVisit = (selectedVisit, selectedOrderId) => {
    // clear the grouping when selecting a single visit.
    this.setState({
      draggingTerminalVisit: []
    })
    // then set the selectedVisit in redux
    this.props.dispatch(setSelectedVisit(selectedVisit, selectedOrderId))
  }

  handleTimeChange = (startTime, endTime) => {
    // bug within TerminalViewer sometimes causes this function to be called with invalid dates
    // See: https://trello.com/c/re2dXORJ/214-3-bug-invariant-violation-on-maitenance-drag
    if (isNaN(startTime) || isNaN(endTime)) {
      return
    }

    if (
      moment(startTime).startOf('day').isBefore(moment(this.props.app.present.dateRange.from).startOf('day')) ||
      moment(endTime).endOf('day').isAfter(moment(this.props.app.present.dateRange.to).endOf('day'))
    ) {
      this.props.dispatch(
        onDateChange(
          { from: startTime, to: endTime },
          this.props.app.present.dataRange,
          this.props.login.auth.terminalId,
          this.props.app.present.config,
          this.props.login.auth.token,
          this.props.customFetch
        )
      )
    } else {
      this.setState({ startTime, endTime })
    }
  }

  /**
   * Zoom in or out
   * @param {number} scale
   * For example 0.9 or 1.1 typically the position of the mouse cursor
   */
  handleZoom = scale => {
    const start = this.state.startTime.valueOf()
    const end = this.state.endTime.valueOf()
    const center = (start + end) / 2
    const newStart = center + (start - center) * scale
    const newEnd = center + (end - center) * scale

    this.handleTimeChange(newStart, newEnd)
    this.props.dispatch(setZoomLevel(newEnd - newStart))
  }

  /**
   * Snap to given zoomlevel
   * @param zoomLevel
   */
  handleSnapToZoomLevel = zoomLevel => {
    /** @type {object} */ let startTime = moment(this.state.startTime).toDate()
    /** @type {object} */ let endTime = moment(this.state.endTime).toDate()

    // Add to the end of current visible timeframe depending on what zoomlevel we're trying to snap to
    switch (zoomLevel) {
      case 'shift': {
        // Snap to shift starting point
        const closestStartingShift = findClosestStartingShift(moment(startTime), this.props.app.present.config.shifts)

        let startingPoint = cloneDeep(moment(startTime)).hours(closestStartingShift.hour).minutes(0)

        // the closest shift was the previous day
        if (closestStartingShift.hour > moment(startTime).hours()) {
          startingPoint = cloneDeep(moment(startTime)).subtract(1, 'days').hours(closestStartingShift.hour).minutes(0)
        }

        startTime = moment(startingPoint).toDate()
        endTime = moment(startTime).add(8, 'hours').toDate()
        break
      }
      default:
      case 'day':
        startTime = moment(startTime).startOf('day').toDate()
        endTime = moment(startTime).add(1, 'day').toDate()
        break
      case 'week':
        startTime = moment(startTime).startOf('week').add(1, 'days').toDate()
        endTime = moment(startTime).add(1, 'week').toDate()
        break
      case 'month':
        startTime = moment(startTime).startOf('month').toDate()
        endTime = moment(startTime).add(1, 'month').toDate()
        break
    }

    // Set new timeframe start and end
    this.handleTimeChange(startTime, endTime)
    this.props.dispatch(setZoomLevel(moment(endTime).valueOf() - moment(startTime).valueOf()))
  }

  /**
   * Returns the z index of the terminal visit
   * @param terminalVisit
   * @param zIndexMap
   */
  getZIndexForTerminalVisit = (terminalVisit, zIndexMap) => {
    const itemZIndex = zIndexMap.find(item => item && item._id === terminalVisit._id)
    return itemZIndex && itemZIndex.zIndex ? itemZIndex.zIndex : 0
  }

  /**
   * Set the right zIndex for a selected terminal visit so that it always shows on below of other terminal visits
   * @param terminalVisitId
   */
  handleLayerDownTerminalVisit = terminalVisitId => {
    const terminalVisit = this.props.app.present.terminalVisits.find(tv => tv._id === terminalVisitId)

    if (terminalVisit) {
      const minZIndex = this.state.minZIndex - 1
      const newZIndexForId = { _id: terminalVisit._id, zIndex: minZIndex }
      const newZIndexMap = this.createNewZIndexMap(newZIndexForId, this.props.app.present.terminalVisits)
      this.setState({ zIndexMap: newZIndexMap, minZIndex })
    }
  }

  createNewZIndexMap = (newZIndexForId, terminalVisits) => {
    return terminalVisits.map(terminalVisit => {
      if (terminalVisit._id === newZIndexForId._id) {
        return newZIndexForId
      }

      // Item is not currently selected terminalVisit -> just push its own zIndex into zIndexMap
      return this.state.zIndexMap.find(item => item && item._id === terminalVisit._id)
    })
  }

  /**
   * This function is called when the duration of a ParallelTerminalVisit order changes.
   * Order start/end times are updated within ParallelTerminalVisit component - this callback function is required to update the rendering of parallel visits.
   * @param {TerminalVisit} updatedTerminalVisit
   */
  handleParallelVisitChanged = updatedTerminalVisit => {
    // FIXME: this is needed because right now we mutate terminal visits!!
    this.forceUpdate()

    // FIXME: this is misusing the the existing code of draggingTerminalVisit. Instead, implement an `onChangeEnd` callback in the TerminalViewer library
    this.setState({
      // explicitly specify _id, as that's used across this component to determine which orders are being dragged / selected
      //@ts-ignore: TerminalVisit component import overrides typedef with same name
      draggingTerminalVisit: [{ _id: updatedTerminalVisit._id, referenceTerminalVisit: updatedTerminalVisit }]
    })
  }

  /**
   * Invoked with dragging TerminalVisit and ParallelTerminalVisit
   * @param {Object} itemChange - ItemChange object from TerminalViewer component
   * @param {TerminalVisitItem} itemChange.updatedItem
   * @param {TerminalVisitItem} itemChange.initialItem
   * @param {boolean} isParallel Indicates whether ParallelTerminalVisit is being dragged
   */
  handleDragTerminalVisit = ({ updatedItem, initialItem }, isParallel) => {
    const updatedItemCopy = produce(updatedItem, draft => {
      draft.referenceTerminalVisit.orders = getSortedOrders(draft.referenceTerminalVisit)
    })

    const previousDraggingTimePosition = this.state.draggingTimePosition || initialItem.yStart.getTime()
    const previousDraggingBollardPosition = this.state.draggingBollardPosition || Math.round(initialItem.xStart)

    const visitsToUpdate =
      isEmpty(this.state.draggingTerminalVisit) ||
      // clear visit selections if current visit isn't part of grouping
      !this.state.draggingTerminalVisit.find(v => v._id === updatedItemCopy._id)
        ? [updatedItemCopy]
        : cloneDeep(this.state.draggingTerminalVisit)

    const diffTime = updatedItemCopy.yStart.getTime() - previousDraggingTimePosition
    let diffBollard = Math.round(updatedItemCopy.xStart) - previousDraggingBollardPosition

    const canUpdateBollardPos = checkIfBollardPositionShouldUpdate(
      diffBollard,
      visitsToUpdate,
      this.props.app.present.config
    )

    // bollards not updated if a selected visit would be moved outside quay boundaries by drag action
    diffBollard = canUpdateBollardPos ? diffBollard : 0

    const updatedVisits = updateTerminalVisits(
      visitsToUpdate,
      updatedItemCopy,
      diffTime,
      diffBollard,
      this.props.app.present.config,
      isParallel
    )

    this.setState({
      draggingTerminalVisit: updatedVisits,
      draggingTimePosition: updatedItem.yStart.getTime(),
      draggingBollardPosition: Math.round(updatedItem.xStart)
    })
  }

  /**
   * Invoked by TerminalViewer when item is dragged or resized.
   * @param itemChange
   */
  handleChangeTerminalVisit = async itemChange => {
    const { ataTime, atdTime } = itemChange.initialItem.referenceTerminalVisit

    if (isValidTimestamp(ataTime) && !isValidTimestamp(atdTime)) {
      const { ataTime } = itemChange.initialItem.referenceTerminalVisit

      if (itemChange.draggedArea === 'center') {
        const movingDown = itemChange.updatedItem.yStart.getTime() > itemChange.initialItem.yStart.getTime()

        const yS =
          itemChange.updatedItem.yStart.getTime() >= ataTime
            ? itemChange.updatedItem.yStart
            : movingDown
            ? itemChange.updatedItem.yStart
            : itemChange.updatedItem.yStart.getTime() >= itemChange.initialItem.yStart.getTime()
            ? itemChange.updatedItem.yStart
            : new Date(ataTime)

        const sailedItemChange = { ...itemChange, updatedItem: { ...itemChange.updatedItem, yStart: yS } }
        this.handleDragTerminalVisit(sailedItemChange, false)
        return
      }
    }

    if (itemChange.draggedArea === 'center') {
      this.handleDragTerminalVisit(itemChange, false)
      return
    }

    const changedVisit = cloneDeep(itemChange.updatedItem)
    if (!isValidTimestamp(atdTime) && itemChange.draggedArea === 'bottom') {
      const minEndTime =
        changedVisit.referenceOrder.cargoStart +
        (changedVisit.referenceOrder.slackPrior + changedVisit.referenceOrder.slackAfter)

      if (changedVisit.yEnd.getTime() > minEndTime) {
        changedVisit.referenceOrder.cargoEnd = getGridTime(moment(changedVisit.yEnd.getTime()))
      }
    } else if (!isValidTimestamp(atdTime) && itemChange.draggedArea === 'top') {
      const minStartTime =
        changedVisit.referenceOrder.cargoEnd -
        (changedVisit.referenceOrder.slackPrior + changedVisit.referenceOrder.slackAfter)

      if (minStartTime > changedVisit.yStart.getTime()) {
        changedVisit.referenceOrder.cargoStart = getGridTime(moment(changedVisit.yStart.getTime()))
      }
    }

    this.setState({
      draggingTerminalVisit: [changedVisit]
    })
  }

  /**
   * Delete a terminalVisit from the backend
   * @param visitIds
   */
  deleteMultipleTerminalVisits = async visitIds => {
    try {
      const terminalVisits = this.props.app.present.terminalVisits.filter(visit => !visitIds.includes(visit._id))

      this.props.dispatch(setTerminalVisits(terminalVisits))
      // this.props.dispatch(setSelectedVisit(undefined, undefined))
      visitIds.forEach(async visitId => {
        await deleteTerminalVisitFromBackendRest(visitId, this.props.login.auth.token, this.props.customFetch)
      })
    } catch (err) {
      handleFetchError(err)
    }
  }

  /**
   * Toggles visit from grouping selection.
   * @param visit Visit to add/remove from grouping
   */
  handleVisitGrouping = visit => {
    const groupedVisits = cloneDeep(this.state.draggingTerminalVisit) || []

    const visitIndex = groupedVisits.findIndex(v => v._id === visit._id)
    if (visitIndex >= 0) {
      // visit already selected, remove from group
      groupedVisits.splice(visitIndex, 1)
    } else {
      groupedVisits.push(visit)
    }

    this.setState({ draggingTerminalVisit: groupedVisits })
  }

  /**
   * Invoked when dragging stops.
   * Update dragged terminal visits
   */
  handleStopDraggingTerminalVisit = () => {
    if (!isEmpty(this.state.draggingTerminalVisit)) {
      Promise.all(
        this.state.draggingTerminalVisit.map(({ referenceTerminalVisit }) => {
          // Make sure the quay matches with the bollard position
          const updatedTerminalVisit = produce(referenceTerminalVisit, visit => {
            const sortedOrdersStart = sortBy(visit.orders, 'bollardStart')
            const sortedOrdersEnd = sortBy(visit.orders, 'bollardEnd')

            const visitHasNormalQuay = isVisitInNormalQuay(this.props.app.present.config.quays, visit)

            if (visitHasNormalQuay) {
              const closestQuay = getClosestQuayToBollard(
                first(sortedOrdersStart).bollardStart,
                last(sortedOrdersEnd).bollardEnd,
                this.props.app.present.config.quays
              )

              if (closestQuay) {
                visit.quayId = closestQuay.name
              }
            }
          })

          return this.props.dispatch(
            updateTerminalVisitInBackend(updatedTerminalVisit, this.props.login.auth.token, this.props.customFetch)
          )
        })
      )

      this.clearIntermediateDragPositions()
      this.setState({ draggingTerminalVisit: [] })
    }
  }

  clearIntermediateDragPositions = () => {
    this.setState({
      draggingBollardPosition: null,
      draggingTimePosition: null
    })
  }

  /**
   * Toggle active overlays in the terminal view
   */
  handleToggleOverlay = overlayName => {
    const currentActiveOverlays = this.props.app.present.activeOverlays ? this.props.app.present.activeOverlays : []
    let newActiveOverlays = []

    if (currentActiveOverlays.indexOf(overlayName) !== -1) {
      newActiveOverlays = currentActiveOverlays.filter(overlay => overlay !== overlayName)
    } else {
      newActiveOverlays = cloneDeep(currentActiveOverlays)
      newActiveOverlays.push(overlayName)
    }

    this.props.dispatch(setActiveOverlays(newActiveOverlays))
  }

  onToggleFilterBoardBoard = () => {
    this.setState(state => ({ filterBoardBoard: !state.filterBoardBoard }))
  }

  handleClickAddVisit = () => {
    const newVisit = getNewEmptyVisit(this.props.app.present.config, this.props.login.auth.terminalId)
    return this.props.dispatch(setSelectedVisit(newVisit, undefined))
  }

  handleClickAddMaintenance = () => {
    this.props.dispatch(setSelectedMaintenance({}))
  }
}

export default connect(state => state)(TerminalPlanner)
